import React, { useMemo } from "react";
import { Typography } from "@material-ui/core";
import styled from "styled-components";
import {
  ASSESSMENT_REQUIRED_FIELDS,
  ASSESSMENT_COUNTRY_COLUMNS_WIDTH,
  ASSESSMENT_STUDY_COLUMNS_WIDTH,
  FIELD_TYPES,
} from "../constants";
import { Tooltip } from "../../../../app/components";

/*
  This component is responsible for rendering the table header of the assessments table.
  It uses the material ui table cell component.
  It uses the material ui table row component.
  The parent component must pass the remove column width of the table.
  The parent component must pass the type column width of the table.
  The parent component must pass the value of the table.
  The parent component must pass the column count of the table.
  The parent component must pass the field type of the table.
*/
export default function TableHeader({ value, fieldType, readOnly }) {
  const commentType = useMemo(() => {
    if (fieldType === FIELD_TYPES.VISIT) return "Assessment Comments";
    if (fieldType === FIELD_TYPES.COST) return "Cost Comments";
    if (fieldType === FIELD_TYPES.MEDICAL) return "Clinical Strategy Comments";
    if (fieldType === FIELD_TYPES.DATA_MANAGEMENT)
      return "Data Management Comments";
    return "";
  }, [fieldType]);
  return (
    <Row>
      <Cell
        width={ value === 0 ?
          ASSESSMENT_STUDY_COLUMNS_WIDTH.assessmentType :
          ASSESSMENT_COUNTRY_COLUMNS_WIDTH.assessmentType
        }
        variant="header"
      >
        <Typography variant="body1">
          <strong>
            Assessment Type{" "}
            {ASSESSMENT_REQUIRED_FIELDS["assessmentType"] && (
              <RequiredContainer>*</RequiredContainer>
            )}
          </strong>
        </Typography>
      </Cell>
      <Cell
        width={ value === 0 ?
          ASSESSMENT_STUDY_COLUMNS_WIDTH.assessmentName :
          ASSESSMENT_COUNTRY_COLUMNS_WIDTH.assessmentName
        }
        variant="header"
      >
        <Typography variant="body1">
          <strong>
            Assessment Name{" "}
            {ASSESSMENT_REQUIRED_FIELDS["assessmentName"] && (
              <RequiredContainer>*</RequiredContainer>
            )}
          </strong>
        </Typography>
      </Cell>
      {value === 0 && (
        <Cell
          width={ASSESSMENT_STUDY_COLUMNS_WIDTH.assessmentNameMapping}
          variant="header"
        >
          <Typography variant="body1">
            <strong>
              Assessment Name Mapping{" "}
              {ASSESSMENT_REQUIRED_FIELDS["assessmentNameMapping"] && (
                <RequiredContainer>*</RequiredContainer>
              )}
            </strong>
          </Typography>
        </Cell>
      )}
      {value !== 0 && (
        <Cell
          width={ASSESSMENT_COUNTRY_COLUMNS_WIDTH.assessmentCost}
          variant="header"
        >
          <Typography variant="body1">
            <strong>
              Assessment Cost{" "}
              {ASSESSMENT_REQUIRED_FIELDS["assessmentCost"] && (
                <RequiredContainer>*</RequiredContainer>
              )}
            </strong>
          </Typography>
        </Cell>
      )}
      {value === 0 && (
        <>
          <Cell
            width={ASSESSMENT_STUDY_COLUMNS_WIDTH.overhead}
            variant="header"
          >
            <Typography variant="body1">
              <strong style={{ display: "flex", alignItems: "center" }}>
                Overhead
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginLeft: "4px",
                    transform: "translateY(-1px)",
                  }}
                >
                  <Tooltip
                    info="Selected assessments will have overhead calculated at the country and site visit level if an Overhead % is also added for the visit."
                    style={{
                      fontSize: "0.8em", // Adjust this value to make the icon smaller
                      lineHeight: 0,
                    }}
                  />
                </span>{" "}
                {ASSESSMENT_REQUIRED_FIELDS["overhead"] && (
                  <RequiredContainer>*</RequiredContainer>
                )}
              </strong>
            </Typography>
          </Cell>
          <Cell
            width={ readOnly ?
              ASSESSMENT_STUDY_COLUMNS_WIDTH.comments.readOnly :
              ASSESSMENT_STUDY_COLUMNS_WIDTH.comments.editable
            }
            variant="header"
          >
            <Typography variant="body1">
              <strong>{commentType}</strong>
            </Typography>
          </Cell>
          {!readOnly && (<Cell
            width={ASSESSMENT_STUDY_COLUMNS_WIDTH.deleteButton}
            variant="header"
          ></Cell>)}
        </>
      )}
    </Row>
  );
}

const Row = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  display: flex;
  height: 50px;
  background: var(--surface-100);
  border-bottom: ${(p) =>
    p.noBorder ? "none" : "1px solid var(--surface-300)"};
  &:last-child {
    border: none;
  }
`;

const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-100)" : "var(--White)"};
  height: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  width: ${(p) => (p.width ? p.width : "100%")};
  border-right: 1px solid var(--surface-300);
  padding: 5px;
  text-align: center;
  border-left: ${(p) =>
    p.leftBorder ? "1px solid var(--surface-300)" : "none"};
  &:last-child {
    border: ${(p) => (p.rightBorder ? "1px solid var(--surface-300)" : "none")};
  }
  border: ${(p) => (p.fullBorder ? "1px solid var(--surface-300)" : "")};
`;

const RequiredContainer = styled.span`
  color: red;
`;
